import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { translate } from "react-i18next";
import {
	GoogleReCaptchaProvider,
	GoogleReCaptcha,
} from "react-google-recaptcha-v3";
import cx from "classnames";
import { INPUT_NAMES, GOOGLE_RECPATCHA_SITE_KEY } from "../utils/config";
import Step from "../components/Step";
import TranslatedFormGroup from "../components/FormGroup";

import { isValid } from "../utils/validation";

const isIdeagenLearning = () => {
	const queryParams = new URLSearchParams(window.location.search);
	const target = queryParams.get("target");
	return target && target.length > 0 && target === "ideagen-learning";
};

const CompanyStep = ({
	t,
	errors,
	changeHandler,
	submitHandler,
	inputs,
	onPrevious,
	processing,
	errMsg,
}) => {
	const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
	const pendingSubmitRef = useRef(null);

	const handleRecaptchaVerify = useCallback(
		token => {
			changeHandler({
				target: {
					name: INPUT_NAMES.recaptcha,
					value: token,
				},
			});

			if (pendingSubmitRef.current) {
				const event = pendingSubmitRef.current;
				pendingSubmitRef.current = null;
				submitHandler(event);
			}
		},
		[changeHandler, submitHandler]
	);

	const handleSubmit = useCallback(
		e => {
			if (e) {
				e.preventDefault();
			}
			// Fetch a fresh token before submitting, since a token generated earlier
			// while filling out the form may have already expired
			pendingSubmitRef.current = e;
			setRefreshReCaptcha(r => !r);
		},
		[]
	);

	return (
		<form className="d-flex flex-column" onSubmit={handleSubmit} noValidate>
			<p className="h5 mb-3">{t("companyTitle")}</p>
			<TranslatedFormGroup
				valid={isValid(errors[INPUT_NAMES.company])}
				feedback={errors[INPUT_NAMES.company]}
				label={"labels.organisation"}
			>
				<input
					onChange={changeHandler}
					className="form-control"
					type="text"
					name={INPUT_NAMES.company}
					value={inputs[INPUT_NAMES.company]}
					maxLength="255"
					autoFocus
					required
				/>
			</TranslatedFormGroup>
			{isIdeagenLearning() && (
				<TranslatedFormGroup
					valid={isValid(errors[INPUT_NAMES.tenantId])}
					feedback={errors[INPUT_NAMES.tenantId]}
					label={"labels.tenantId"}
				>
					<input
						onChange={changeHandler}
						className="form-control"
						type="text"
						name={INPUT_NAMES.tenantId}
						value={inputs[INPUT_NAMES.tenantId]}
						maxLength="255"
						autoFocus
					/>
				</TranslatedFormGroup>
			)}
			{isIdeagenLearning() && (
				<TranslatedFormGroup
					valid={isValid(errors[INPUT_NAMES.tenantAdminId])}
					feedback={errors[INPUT_NAMES.tenantAdminId]}
					label={"labels.tenantAdminId"}
				>
					<input
						onChange={changeHandler}
						className="form-control"
						type="text"
						name={INPUT_NAMES.tenantAdminId}
						value={inputs[INPUT_NAMES.tenantAdminId]}
						maxLength="255"
						autoFocus
					/>
				</TranslatedFormGroup>
			)}
			<div
				className={cx("form-group mb-4", {
					"has-danger": !isValid(errors[INPUT_NAMES.recaptcha]),
				})}
			>
				<GoogleReCaptchaProvider reCaptchaKey={GOOGLE_RECPATCHA_SITE_KEY}>
					<GoogleReCaptcha
						onVerify={handleRecaptchaVerify}
						refreshReCaptcha={refreshReCaptcha}
					/>
				</GoogleReCaptchaProvider>
				{!isValid(errors[INPUT_NAMES.recaptcha]) && (
					<div className="form-control-feedback">
						{t(errors[INPUT_NAMES.recaptcha])}
					</div>
				)}
			</div>
			{errMsg && <div className="alert alert-danger">{errMsg}</div>}
			<button
				className="btn btn-primary btn-block btn-lg"
				type="submit"
				disabled={processing}
			>
				{t("labels.startUsingVelpic")}
			</button>
			<button
				onClick={onPrevious}
				className="btn btn-block btn-link"
				disabled={processing}
			>
				{t("labels.previous")}
			</button>
		</form>
	);
};

CompanyStep.propTypes = {
	t: PropTypes.func,
	errors: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
	onPrevious: PropTypes.func,
	changeHandler: PropTypes.func,
	submitHandler: PropTypes.func,
	inputs: PropTypes.object,
	processing: PropTypes.bool,
	errMsg: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default translate()(
	Step(CompanyStep, {
		[INPUT_NAMES.company]: "",
		[INPUT_NAMES.recaptcha]: "",
		[INPUT_NAMES.tenantId]: "",
		[INPUT_NAMES.tenantAdminId]: "",
	})
);
